import {Card, DataStatus} from 'platform/components';
import {Show, Space} from 'platform/foundation';

import {ReactElement} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {useGetPlatformsQuery, useUpdatePlatformMutation} from '@dms/api/sales';
import {useIsPublishingMultiBranchEnabledQuery} from '@dms/api/settings';
import {featureFlags} from '@dms/feature-flags';
import {testIds} from '@dms/routes';
import {useBranchId} from '@dms/settings/shared';
import {
  AiVehicleDescriptionBanner,
  fakePlatforms,
  isAdvertisementPlatformEnabled,
  usePermissions,
} from '@dms/shared';

import {AdvertisementPlatformRow} from './AdvertisementPlatformRow';
import {AutomaticUpdateControl} from './AutomaticUpdateControl';
import {DescriptionGeneratorControl} from './DescriptionGeneratorControl';
import {MultiBranchControl} from './MultiBranchControl';

export function GeneralAdvertisementPlatforms() {
  const [canConfigureDescriptionGenerator] = usePermissions({
    permissionKeys: ['configureDescriptionGenerator'],
  });
  const branchId = useBranchId();
  const {data: isMultiBranch} = useIsPublishingMultiBranchEnabledQuery();
  const isSettingsSynchronised = isMultiBranch === isNotNil(branchId);

  const {data: platforms, isLoading} = useGetPlatformsQuery(
    {branchId},
    {skip: !isSettingsSynchronised}
  );

  const [updateStatus] = useUpdatePlatformMutation();

  return (
    <>
      <Show whenFeatureEnabled={featureFlags.SETTINGS_ADVERTISEMENT_AUTOMATIC_UPDATE}>
        <AutomaticUpdateControl
          data-testid={testIds.settings.advertisingPlatforms('automaticUpdateControl')}
        />
        <Space vertical={4} />
      </Show>
      <Show whenFeatureEnabled={featureFlags.SETTINGS_ADVERTISEMENT_MULTIBRANCH}>
        <MultiBranchControl />
        <Space vertical={4} />
      </Show>
      <Show when={canConfigureDescriptionGenerator}>
        <AiVehicleDescriptionBanner />
        <Space vertical={4} />
        <DescriptionGeneratorControl />
        <Space vertical={4} />
      </Show>
      <Card>
        <DataStatus isLoading={isLoading || !isSettingsSynchronised}>
          {platforms
            ?.filter(isAdvertisementPlatformEnabled)
            ?.map((platform, index): ReactElement => (
              <AdvertisementPlatformRow
                key={platform.code}
                platform={platform}
                isFirst={index === 0}
                handleStatusChange={(platformCode, enabled) => {
                  updateStatus({platformCode, enabled, branchId});
                }}
              />
            ))}
          <Show whenFeatureEnabled={featureFlags.ADVERTISEMENT_FAKE_PLATFORMS}>
            {fakePlatforms.map((platform) => (
              <AdvertisementPlatformRow
                key={platform.code}
                platform={platform}
                isFake
                handleStatusChange={() => {}}
              />
            ))}
          </Show>
        </DataStatus>
      </Card>
    </>
  );
}
